import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Snackbar,
  Fade,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  InputAdornment,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  SmartToy as BotIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Psychology as PsychologyIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useUserRole } from '../../contexts/UserRoleContext';
import { apiRequest } from '../../utils/apiConfig';
import { getPublicWidgetBaseUrl } from '../../utils/publicUrls';

const ChatSessions = () => {
  const { isAdmin, isCustomer, userCompany } = useUserRole();
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('Du bist ein hilfsreicher KI-Assistent.');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [openrouterApiKey, setOpenrouterApiKey] = useState('');
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // Lokales RAG-System als Standard
  const localRAGModel = {
    id: 'local-rag',
    name: 'Lokales RAG-System',
    provider: 'Mein Ticketsystem',
    description: 'Ihr lokales KI-System mit RAG-Funktionalität',
    cost: 'Kostenlos',
    maxTokens: 'Unbegrenzt',
    endpoint: 'ki.meinticketsystem.de',
    color: 'success'
  };

  // Standard-OpenRouter-Modell
  const defaultOpenRouterModel = {
    id: 'openai/gpt-3.5-turbo',
    name: 'GPT-3.5 Turbo',
    provider: 'OpenAI via OpenRouter',
    description: 'Schnelles und kostengünstiges Modell für allgemeine Aufgaben',
    cost: '$0.0015/1K Input, $0.002/1K Output',
    maxTokens: 4096,
    endpoint: 'openrouter.ai',
    color: 'primary'
  };

  // Alle verfügbaren Modelle
  const allAvailableModels = [localRAGModel, defaultOpenRouterModel, ...openrouterModels];

  useEffect(() => {
    try {
      loadSessions();
      // Lade gespeicherten API Key
      const savedApiKey = localStorage.getItem('openrouter_api_key');
      if (savedApiKey) {
        setOpenrouterApiKey(savedApiKey);
        loadOpenrouterModels(savedApiKey);
      }
      
      // Event Listener für API Key Updates
      const handleApiKeyUpdate = (event) => {
        const { apiKey } = event.detail;
        setOpenrouterApiKey(apiKey);
        if (apiKey) {
          loadOpenrouterModels(apiKey);
        } else {
          setOpenrouterModels([]);
        }
      };
      
      window.addEventListener('openrouterApiKeyUpdated', handleApiKeyUpdate);
      
      return () => {
        window.removeEventListener('openrouterApiKeyUpdated', handleApiKeyUpdate);
      };
    } catch (error) {
      console.error('Fehler beim Initialisieren der Chat Sessions:', error);
      setError('Fehler beim Laden der Chat Sessions: ' + error.message);
    }
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/chat-sessions`);
      setSessions(data);
    } catch (error) {
      console.error('Fehler beim Laden der Sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOpenrouterModels = async (apiKey) => {
    if (!apiKey) return;
    
    setLoadingModels(true);
    try {
      const data = await apiRequest(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/openrouter/models`, {
        method: 'POST',
        body: JSON.stringify({ api_key: apiKey })
      });
      
      if (data.success && data.models) {
        const formattedModels = data.models.map(model => ({
          id: model.id,
          name: model.name || model.id,
          provider: model.id.split('/')[0] || 'Unknown',
          description: model.description || 'Keine Beschreibung verfügbar',
          cost: 'Siehe OpenRouter Pricing',
          maxTokens: model.context_length || 0,
          color: 'info'
        }));
        
        setOpenrouterModels(formattedModels);
        setSuccess('OpenRouter Modelle erfolgreich geladen!');
      } else {
        throw new Error(data.message || 'Keine Modelle gefunden');
      }
    } catch (error) {
      console.error('Fehler beim Laden der OpenRouter Modelle:', error);
      setError('Fehler beim Laden der OpenRouter Modelle: ' + error.message);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleApiKeySubmit = () => {
    if (!openrouterApiKey.trim()) {
      setError('Bitte geben Sie einen API Key ein.');
      return;
    }
    
    localStorage.setItem('openrouter_api_key', openrouterApiKey);
    setApiKeyDialogOpen(false);
    loadOpenrouterModels(openrouterApiKey);
  };

  const clearApiKey = () => {
    localStorage.removeItem('openrouter_api_key');
    setOpenrouterApiKey('');
    setOpenrouterModels([]);
    setSuccess('API Key entfernt.');
  };

  const handleCreateSession = async () => {
    if (!selectedModel || !sessionName.trim()) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/chat-sessions`, {
        method: 'POST',
        body: JSON.stringify({
          name: sessionName,
          model: selectedModel,
          system_prompt: systemPrompt,
          api_key: selectedModel !== 'local-rag' ? openrouterApiKey : null,
        }),
      });
      
      const sessionId = data.session_id || data.id;
      const chatLink = `${getPublicWidgetBaseUrl()}/chat/session/${sessionId}`;
    
      const newSession = {
        id: sessionId,
        name: sessionName,
        model: selectedModel,
        systemPrompt: systemPrompt,
        chatLink: chatLink,
        createdAt: new Date().toISOString(),
        messageCount: 0,
        isActive: true,
      };

      setSessions(prev => [newSession, ...prev]);
      setGeneratedLink(chatLink);
      setSuccess('Chat-Session erfolgreich erstellt!');
      setCreateDialogOpen(false);
      
      // URL-Popup anzeigen
      setGeneratedUrl(chatLink);
      setUrlDialogOpen(true);
      
      // Reset form
      setSessionName('');
      setSystemPrompt('Du bist ein hilfsreicher KI-Assistent.');
      setSelectedModel('');
    } catch (error) {
      setError('Fehler beim Erstellen der Session: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setSessionName(session.name);
    setSystemPrompt(session.systemPrompt || 'Du bist ein hilfsreicher KI-Assistent.');
    setSelectedModel(session.model);
    setEditDialogOpen(true);
  };

  const handleUpdateSession = async () => {
    if (!editingSession) return;

    setLoading(true);
    setError(null);

    try {
      await apiRequest(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/chat-sessions/${editingSession.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: sessionName,
          model: selectedModel,
          system_prompt: systemPrompt,
        }),
      });
      
      setSessions(prev => prev.map(s => 
        s.id === editingSession.id 
          ? { ...s, name: sessionName, model: selectedModel, systemPrompt: systemPrompt }
          : s
      ));
      setSuccess('Session erfolgreich aktualisiert!');
      setEditDialogOpen(false);
      setEditingSession(null);
    } catch (error) {
      setError('Fehler beim Aktualisieren der Session: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Link in Zwischenablage kopiert!');
      setTimeout(() => setSuccess(null), 3000);
    });
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Session löschen möchten?')) {
      return;
    }

    try {
      await apiRequest(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/chat-sessions/${sessionId}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      setSuccess('Session gelöscht!');
    } catch (error) {
      setError('Fehler beim Löschen der Session: ' + error.message);
    }
  };

  const getModelInfo = (modelId) => {
    if (modelId === 'local-rag') {
      return localRAGModel;
    }
    
    return openrouterModels.find(m => m.id === modelId) || {
      name: modelId,
      provider: 'Unknown',
      description: 'Unbekanntes Modell',
      cost: 'Unbekannt',
      color: 'default'
    };
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModel = !filterModel || session.model === filterModel;
    const matchesStatus = showInactive || session.isActive;
    return matchesSearch && matchesModel && matchesStatus;
  });

  const handleMenuOpen = (event, session) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSession(null);
  };

  const getModelColor = (modelId) => {
    const model = getModelInfo(modelId);
    return model.color || 'default';
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusIcon = (isActive) => {
    return isActive ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <ChatIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Chat Sessions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Verwalten Sie Ihre KI-Chat-Sessions
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSessions}
            disabled={loading}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Neue Session
          </Button>
        </Box>
      </Box>

      {/* Filter und Suche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Sessions durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Modell filtern</InputLabel>
                <Select
                  value={filterModel}
                  onChange={(e) => setFilterModel(e.target.value)}
                  label="Modell filtern"
                >
                  <MenuItem value="">Alle Modelle</MenuItem>
                  {allAvailableModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                }
                label="Inaktive Sessions anzeigen"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setFilterModel('');
                  setShowInactive(false);
                }}
                fullWidth
              >
                Filter zurücksetzen
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sessions Tabelle */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>Session Name</strong></TableCell>
                <TableCell><strong>Modell</strong></TableCell>
                <TableCell><strong>System Prompt</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Nachrichten</strong></TableCell>
                <TableCell><strong>Erstellt</strong></TableCell>
                <TableCell align="center"><strong>Aktionen</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSessions.map((session) => {
                const modelInfo = getModelInfo(session.model);
                return (
                  <TableRow key={session.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <ChatIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {session.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {session.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={modelInfo.name}
                        color={getModelColor(session.model)}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {modelInfo.provider}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={session.systemPrompt || 'Kein System Prompt'}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 200, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {session.systemPrompt || 'Kein System Prompt'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(session.isActive)}
                        label={session.isActive ? 'Aktiv' : 'Inaktiv'}
                        color={getStatusColor(session.isActive)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={session.messageCount} color="primary">
                        <ChatIcon color="action" />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(session.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Chat öffnen">
                          <IconButton
                            size="small"
                            onClick={() => window.open(session.chatLink, '_blank')}
                            color="primary"
                          >
                            <OpenIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Link kopieren">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(session.chatLink)}
                            color="info"
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            size="small"
                            onClick={() => handleEditSession(session)}
                            color="warning"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mehr">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, session)}
                            color="default"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredSessions.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ChatIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filterModel ? 'Keine Sessions gefunden' : 'Noch keine Sessions vorhanden'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || filterModel 
                ? 'Versuchen Sie andere Suchkriterien' 
                : 'Erstellen Sie Ihre erste Chat-Session'
              }
            </Typography>
          </Box>
        )}
      </Card>

      {/* Create Session Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Neue Chat-Session erstellen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Session Name"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder="z.B. Fahrrad-Beratung"
              fullWidth
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>KI-Modell auswählen</InputLabel>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                label="KI-Modell auswählen"
              >
                {allAvailableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {model.name}
                        </Typography>
                        <Chip
                          label={model.cost}
                          size="small"
                          color={model.id === 'local-rag' ? 'success' : 'primary'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {model.provider} • {model.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.id === 'local-rag' 
                          ? `Endpoint: ${model.endpoint}`
                          : `Max. Tokens: ${typeof model.maxTokens === 'number' ? model.maxTokens.toLocaleString() : model.maxTokens}`
                        }
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="System Prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder="Definieren Sie, wie sich der KI-Assistent verhalten soll..."
              helperText="Anweisungen für das KI-Modell. Beschreiben Sie die Rolle und das Verhalten des Assistenten."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleCreateSession} 
            variant="contained" 
            disabled={loading || !selectedModel || !sessionName.trim()}
          >
            {loading ? 'Erstelle...' : 'Session erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Session bearbeiten
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Session Name"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              fullWidth
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>KI-Modell auswählen</InputLabel>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                label="KI-Modell auswählen"
              >
                {allAvailableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {model.name}
                        </Typography>
                        <Chip
                          label={model.cost}
                          size="small"
                          color={model.id === 'local-rag' ? 'success' : 'primary'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {model.provider} • {model.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="System Prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Anweisungen für das KI-Modell. Beschreiben Sie die Rolle und das Verhalten des Assistenten."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleUpdateSession} 
            variant="contained" 
            disabled={loading || !selectedModel || !sessionName.trim()}
          >
            {loading ? 'Aktualisiere...' : 'Session aktualisieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedSession) {
            copyToClipboard(selectedSession.chatLink);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Link kopieren</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSession) {
            window.open(selectedSession.chatLink, '_blank');
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <OpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chat öffnen</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSession) {
            handleEditSession(selectedSession);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bearbeiten</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedSession) {
            deleteSession(selectedSession.id);
          }
          handleMenuClose();
        }} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Löschen</ListItemText>
        </MenuItem>
      </Menu>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)}>
        <DialogTitle>OpenRouter API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="API Key"
            type="password"
            value={openrouterApiKey}
            onChange={(e) => setOpenrouterApiKey(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Geben Sie Ihren OpenRouter API Key ein, um zusätzliche Modelle zu laden."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleApiKeySubmit} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* URL Popup Dialog */}
      <Dialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Chat-Session erfolgreich erstellt!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Ihre Chat-Session ist jetzt verfügbar. Hier ist der direkte Link:
          </Typography>
          
          <TextField
            fullWidth
            value={generatedUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyToClipboard(generatedUrl)}>
                  <CopyIcon />
                </IconButton>
              )
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              onClick={() => window.open(generatedUrl, '_blank')}
            >
              Chat testen
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(generatedUrl)}
            >
              Link kopieren
            </Button>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tipp:</strong> Sie können diesen Link direkt teilen oder in Ihre Website einbetten. 
              Der Chat funktioniert ohne Anmeldung.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatSessions;